import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const headingVariants = (delay = 0) => ({
  hidden: { y: "110%" },
  show: { y: 0, transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] } },
});

function Lined({ text, delay = 0 }) {
  if (!text) return null;
  return (
    <span className="inline-block overflow-hidden align-bottom">
      <motion.span className="inline-block" variants={headingVariants(delay)} initial="hidden" animate="show">
        {text}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/hero").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return <section id="top" className="min-h-screen" data-testid="hero-section" />;
  }

  return (
    <section id="top" className="relative min-h-screen pt-32 md:pt-40 px-6 md:px-12 lg:px-24 flex flex-col justify-between" data-testid="hero-section">
      <div className="flex-1 flex flex-col justify-center">
        {data.overline && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-neutral-500 mb-8 md:mb-12"
            data-testid="hero-overline"
          >
            {data.overline}
          </motion.p>
        )}

        <h1 className="font-display font-black uppercase leading-[0.88] tracking-tighter text-[14vw] sm:text-[12vw] lg:text-[9.5vw]" data-testid="hero-headline">
          <div><Lined text={data.line1} delay={0} /></div>
          {data.accent1 && (
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-serif-italic font-light normal-case lowercase text-[#FF3E00]">
                <Lined text={data.accent1} delay={0.45} />
              </span>
            </div>
          )}
          <div><Lined text={data.line2} delay={0.6} /></div>
          {data.accent2 && (
            <div className="flex items-baseline gap-3 flex-wrap">
              <span className="font-serif-italic font-light normal-case lowercase">
                <Lined text={data.accent2} delay={1.05} />
              </span>
            </div>
          )}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 pb-10 md:pb-20 mt-10">
        {data.paragraph && (
          <div className="md:col-span-5 md:col-start-7">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.7 }}
              className="font-body text-base sm:text-lg leading-relaxed text-neutral-700"
              data-testid="hero-paragraph"
            >
              {data.paragraph}
            </motion.p>
          </div>
        )}
        {data.scroll_hint && (
          <div className="md:col-span-3 md:col-start-1 md:row-start-1 flex items-end">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.6 }}
              className="font-body text-xs uppercase tracking-[0.2em] text-neutral-500"
              data-testid="hero-scroll-hint"
            >
              {data.scroll_hint}
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
}
